import { captureException } from '@sentry/react';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';
import { Feedback } from '@/features/feedback/feedback.types';
import { cn } from '@/lib/utils';
import { userStore } from '@/stores/user.store';
import { submitSurvey } from '@/utils/analytics';
import { logoutUser } from '@/utils/auth';

import { Button } from '../ui/Button';
import { Checkbox } from '../ui/Checkbox';
import { Dialog } from '../ui/dialog';
import { Label } from '../ui/Label';
import { YStack } from '../ui/layout/layout';
import TextArea from '../ui/TextArea';

import { ModalsManager, type ResolvableModalFC } from './modalsManager';

interface DeleteAccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resolve: (value: boolean) => void;
}

const reasons = [
  {
    value: 'not-working',
    label: "It's not working correctly for me",
    subReasons: [
      'The app is slow or crashes often',
      "It doesn't capture my activity correctly",
      'The AI gives inaccurate or "made-up" answers (hallucinations)',
      'My integrations (e.g., Calendar) are broken',
    ],
  },
  {
    value: 'not-valuable',
    label: "I'm not getting the value I expected",
    subReasons: [
      "The AI's answers are often generic or low-quality",
      "I'm not sure how to ask the right questions",
      "It doesn't have the right context about my work",
      'Using Littlebird takes more effort than my old workflow',
      "It's missing a key feature I need",
    ],
  },
  {
    value: 'privacy-concerns',
    label: 'I have privacy concerns',
    subReasons: [
      "I'm worried about how my data is stored and secured",
      "I don't want Littlebird to see sensitive information (e.g. financial data on screen)",
      "I'm unclear on who has access to my data",
      "I'm concerned about my data being used to train AI models",
      'The tool captures more information than I am comfortable with',
    ],
  },
  {
    value: 'something-else',
    label: 'Something else',
    subReasons: [],
  },
];

export const DeleteAccountModal: ResolvableModalFC<
  DeleteAccountModalProps,
  boolean
> = observer(({ open, onOpenChange, resolve }) => {
  const [primaryReason, setPrimaryReason] = useState<string>('');
  const [subReasons, setSubReasons] = useState<string[]>([]);
  const [otherReasonText, setOtherReasonText] = useState('');
  const hasConfirmed = useRef(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const user = userStore.user;
  const email = user?.email;

  const selectedReason = reasons.find((r) => r.value === primaryReason);
  const isSomethingElse = primaryReason === 'something-else';

  const getFeedbackSummary = () => {
    if (!primaryReason) return '';

    let summary = `Feedback submitted:\n\nPrimary reason:\n- ${selectedReason?.label}\n`;

    if (isSomethingElse && otherReasonText.trim()) {
      summary += `\nDetails:\n${otherReasonText.trim()}\n`;
    } else if (subReasons.length > 0) {
      summary += `\nSpecific issues:\n${subReasons.map((r) => `  • ${r}`).join('\n')}\n`;
    }

    return `${summary}\n\n=== Info === \n- App version: ${__APP_VERSION__}\n`;
  };

  const resolveParent = async (value: boolean) => {
    hasConfirmed.current = true;
    if (value) {
      if (!primaryReason) {
        toast.error('Please select a reason for deleting your account.');
        return;
      }
      if (!isSomethingElse && subReasons.length === 0) {
        toast.error('Please select at least one specific issue.');
        return;
      }
      try {
        setIsDeleting(true);
        const feedbackSummary = getFeedbackSummary();
        submitSurvey('feedback & support', [
          'account_deletion',
          email ?? 'anonymous',
          feedbackSummary ?? 'Did not leave any feedback',
        ]).catch(captureException);
        try {
          const { status, message } = await backendApis.feedback.submitFeedback(
            Feedback.AccountDeletion,
            feedbackSummary ?? 'Did not leave any feedback'
          );
          if (status === 'error') {
            captureException(new Error(message));
          }
        } catch (error) {
          captureException(error);
        }
        await backendApis.user.deleteUser();
        await logoutUser();
        ModalsManager.hideAll();
        resolve(true);
      } catch (error) {
        captureException(error);
        toast.error('Failed to delete account. Please try again later.');
        setIsDeleting(false);
        resolve(false);
      }
    } else {
      onOpenChange(false);
      resolve(false);
    }
  };

  const handleOpenChange = async (open: boolean) => {
    if (!open && !hasConfirmed.current) await resolveParent(false);
    onOpenChange(open);
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content className="gap-6 max-w-xl">
        <Dialog.Header className="text-left">
          <Dialog.Title>We're sorry to see you go!</Dialog.Title>
          <Dialog.Description>
            To help us improve, please let us know the primary reason you're
            canceling your account.
          </Dialog.Description>
        </Dialog.Header>

        <YStack className="gap-4">
          {reasons.map((reason) => (
            <YStack key={reason.value} className="gap-2">
              <Button
                size="sm"
                variant="outline"
                className={cn(
                  'justify-start text-left h-auto py-2.5 px-3 transition-colors border-border-strong',
                  primaryReason === reason.value &&
                    'border-accent hover:enabled:border-strongest'
                )}
                onClick={() => {
                  setPrimaryReason(reason.value);
                  setSubReasons([]);
                  setOtherReasonText('');
                }}
              >
                {reason.label}
              </Button>

              {primaryReason === reason.value && !!reason.subReasons.length && (
                <YStack className="gap-2 pl-2">
                  <Label className="text-sm text-foreground-subtle">
                    Please select all that apply:
                  </Label>
                  {reason.subReasons.map((subReason) => (
                    <label
                      key={subReason}
                      className="flex items-start gap-2 cursor-pointer"
                      htmlFor={`sub-reason-${subReason}`}
                    >
                      <Checkbox
                        id={`sub-reason-${subReason}`}
                        checked={subReasons.includes(subReason)}
                        onCheckedChange={() =>
                          setSubReasons((prev) =>
                            prev.includes(subReason)
                              ? prev.filter((r) => r !== subReason)
                              : [...prev, subReason]
                          )
                        }
                        className="mt-0.5 bg-background-subtle"
                      />
                      <span className="text-sm text-foreground">
                        {subReason}
                      </span>
                    </label>
                  ))}
                </YStack>
              )}

              {primaryReason === reason.value &&
                reason.value === 'something-else' && (
                  <YStack className="gap-1.5 pl-3">
                    <Label
                      htmlFor="other-reason"
                      className="text-sm text-foreground-subtle"
                    >
                      Could you tell us a bit more? (Optional)
                    </Label>
                    <TextArea
                      id="other-reason"
                      className="h-24"
                      value={otherReasonText}
                      onChange={(e) => setOtherReasonText(e.target.value)}
                      placeholder="Please describe..."
                    />
                  </YStack>
                )}
            </YStack>
          ))}
        </YStack>

        <Dialog.Footer className="mt-2 gap-1">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              resolveParent(false);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={
              isDeleting ||
              !primaryReason ||
              (!isSomethingElse && subReasons.length === 0)
            }
            variant="destructive"
            onClick={() => resolveParent(true)}
          >
            {isDeleting ? 'Deleting...' : 'Delete account'}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
});
